import React, { useState, useEffect } from 'react';
import { initializeSettings, db } from './lib/database';
import ProductsPage from './components/ProductsPage';
import SettingsPage from './components/SettingsPage';
import ProductForm from './components/ProductForm';
import { Settings, Package2, Plus } from 'lucide-react';
import './App.css';

function App() {
  const [currentPage, setCurrentPage] = useState('products');
  const [isLoading, setIsLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [closeModalsCount, setCloseModalsCount] = useState(0);

  useEffect(() => {
    const initDB = async () => {
      try {
        await initializeSettings();
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to initialize database:', error);
        setIsLoading(false);
      }
    };

    initDB();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Package2 className="h-12 w-12 mx-auto mb-4 text-primary" />
          <h1 className="text-2xl font-bold mb-2">Samsun Jeneratör Ürünlerim</h1>
          <p className="text-muted-foreground">Uygulama başlatılıyor...</p>
        </div>
      </div>
    );
  }

  const handleProductSaved = () => {
    setShowProductForm(false);
    // Trigger refresh in ProductsPage
    setRefreshTrigger(prev => prev + 1);
    // If we're not on the products page, navigate to it to see the new product
    if (currentPage !== 'products') {
      setCurrentPage('products');
    }
  };

  const handleNavigateToProducts = () => {
    // Close any open modals and navigate to products
    setShowProductForm(false);
    setCloseModalsCount(prev => prev + 1);
    setCurrentPage('products');
  };

  const handleNavigateToSettings = () => {
    // Close any open modals and navigate to settings
    setShowProductForm(false);
    setCloseModalsCount(prev => prev + 1);
    setCurrentPage('settings');
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header - Simplified */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <Package2 className="h-6 w-6 text-primary" />
              <h1 className="text-xl font-bold">Samsun Jeneratör Ürünlerim</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {currentPage === 'products' && <ProductsPage key={refreshTrigger} closeModalsCount={closeModalsCount} onAddProduct={() => setShowProductForm(true)} />}
        {currentPage === 'settings' && <SettingsPage />}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-md border-t border-border mobile-nav z-[100] pointer-events-auto">
        <div className="grid grid-cols-3 h-16">
          <button
            className={`flex flex-col items-center justify-center space-y-1 mobile-nav-button transition-colors ${
              currentPage === 'products' ? 'text-primary' : 'text-muted-foreground'
            }`}
            onClick={handleNavigateToProducts}
          >
            <Package2 className="h-5 w-5" />
            <span className="text-xs font-medium">Ürünler</span>
          </button>
          
          <button
            className="flex flex-col items-center justify-center space-y-1 mobile-nav-button transition-colors text-muted-foreground hover:text-foreground"
            onClick={() => setShowProductForm(true)}
          >
            <Plus className="h-5 w-5" />
            <span className="text-xs font-medium">Ekle</span>
          </button>
          
          <button
            className={`flex flex-col items-center justify-center space-y-1 mobile-nav-button transition-colors ${
              currentPage === 'settings' ? 'text-primary' : 'text-muted-foreground'
            }`}
            onClick={handleNavigateToSettings}
          >
            <Settings className="h-5 w-5" />
            <span className="text-xs font-medium">Ayarlar</span>
          </button>
        </div>
      </nav>

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductForm
          onSave={handleProductSaved}
          onCancel={() => setShowProductForm(false)}
        />
      )}
    </div>
  );
}

export default App;